import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, SearchX, X } from "lucide-react";
import { ResultsTopBar } from "@/components/ResultsTopBar";
import { Footer } from "@/components/Footer";
import { FilterBar } from "@/components/FilterBar";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { PropertyCard } from "@/components/PropertyCard";
import { Pagination } from "@/components/Pagination";
import { MapResultsLayout } from "@/components/map/MapResultsLayout";
import { ResultsMap } from "@/components/map/ResultsMap";
import { fetchListings } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { slotRank, ageInDays, isVerifiedCover } from "@/lib/format";
import { isMappable, listingsForDrawer } from "@/lib/geo";

const PAGE_SIZE = 32;

const FILTER_KEYS = [
  "dealType", "city", "district", "metro", "metroDist",
  "rooms", "bedrooms", "wcSeparate", "bathTub",
  "areaMin", "areaMax", "kitchenMin", "kitchenMax", "livingMin", "livingMax",
  "priceMin", "priceMax", "noCommission", "propertyType",
  "parking", "undergroundOnly", "furnished", "pets", "availableFrom",
  "builtFrom", "builtTo", "publication", "cargoLift", "onlyGold", "onlyVerified", "directOwner", "amenities", "kidsAllowed",
];

const num = (v) => (v === "" || v == null ? null : Number(v));

export default function Results() {
  const { t } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => {
    const f = {};
    FILTER_KEYS.forEach((k) => { f[k] = searchParams.get(k) || ""; });
    return f;
  }, [searchParams]);

  const sort = searchParams.get("sort") || "gold";
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);
  const mapView = searchParams.get("view") === "map";
  const focusId = searchParams.get("focus") || "";

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    next.delete("page");
    next.delete("focus");
    setSearchParams(next, { replace: true });
  };

  const setSort = (value) => {
    const next = new URLSearchParams(searchParams);
    next.set("sort", value);
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const setView = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("view", value); else next.delete("view");
    next.delete("page");
    next.delete("focus");
    setSearchParams(next, { replace: true });
    if (!value) setLockedIds(null);
  };

  const openOnMap = (listing) => {
    if (!isMappable(listing)) return;
    const next = new URLSearchParams(searchParams);
    next.set("view", "map");
    next.set("focus", listing.id);
    next.delete("page");
    setSearchParams(next, { replace: true });
    setLockedIds([listing.id]);
    setMapFocusNonce((n) => n + 1);
  };

  const clearMapSelection = () => {
    setLockedIds(null);
    const next = new URLSearchParams(searchParams);
    next.delete("focus");
    setSearchParams(next, { replace: true });
  };

  const resetFilters = () => {
    const next = new URLSearchParams();
    if (searchParams.get("sort")) next.set("sort", searchParams.get("sort"));
    if (mapView) next.set("view", "map");
    setSearchParams(next, { replace: true });
  };

  const setPage = (p) => {
    const next = new URLSearchParams(searchParams);
    if (p <= 1) next.delete("page"); else next.set("page", String(p));
    setSearchParams(next, { replace: false });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const { data, isLoading } = useQuery({ queryKey: ["listings"], queryFn: () => fetchListings(500, 0), refetchOnWindowFocus: false });

  const filtered = useMemo(() => {
    const items = data?.items || [];
    const goldOn = filters.onlyGold !== "0";
    let list = items.filter((it) => {
      const s = it.specs || {};
      const f = it.features || {};

      if (goldOn && it.gold?.isGold !== true) return false;
      if (filters.onlyVerified === "1" && !isVerifiedCover(it)) return false;
      if (filters.dealType && it.listingType !== filters.dealType) return false;
      if (filters.propertyType && it.propertyType !== filters.propertyType) return false;
      if (filters.city && !(it.location?.city || "").toLowerCase().includes(filters.city.toLowerCase())) return false;
      if (filters.district && !((it.location?.address || "") + (it.location?.city || "")).toLowerCase().includes(filters.district.toLowerCase())) return false;
      if (filters.metro && !((it.metro?.name || "").toLowerCase().includes(filters.metro.toLowerCase()))) return false;
      if (num(filters.metroDist) != null && !(it.metro && it.metro.distanceMeters <= num(filters.metroDist))) return false;

      const rooms = s.rooms ?? 0;
      if (filters.rooms !== "") { if (filters.rooms === "0" ? rooms !== 0 : rooms < num(filters.rooms)) return false; }
      if (num(filters.bedrooms) != null && (s.bedrooms ?? 0) < num(filters.bedrooms)) return false;
      if (filters.wcSeparate === "1" && !s.wcSeparate) return false;
      if (filters.bathTub === "1" && !s.bathWithTub) return false;

      if (num(filters.areaMin) != null && (s.area ?? 0) < num(filters.areaMin)) return false;
      if (num(filters.areaMax) != null && (s.area ?? Infinity) > num(filters.areaMax)) return false;
      if (num(filters.kitchenMin) != null && (s.kitchenArea ?? 0) < num(filters.kitchenMin)) return false;
      if (num(filters.kitchenMax) != null && (s.kitchenArea ?? Infinity) > num(filters.kitchenMax)) return false;
      if (num(filters.livingMin) != null && (s.livingArea ?? 0) < num(filters.livingMin)) return false;
      if (num(filters.livingMax) != null && (s.livingArea ?? Infinity) > num(filters.livingMax)) return false;

      const amount = it.price?.amount;
      if (num(filters.priceMin) != null && (amount == null || amount < num(filters.priceMin))) return false;
      if (num(filters.priceMax) != null && (amount == null || amount > num(filters.priceMax))) return false;
      if (filters.noCommission === "1" && !f.noCommission) return false;

      const hasParking = (f.parkingOutdoor || 0) + (f.parkingUnderground || 0) > 0 || s.parking;
      if (filters.parking === "1" && !hasParking) return false;
      if (filters.undergroundOnly === "1" && !(f.parkingUnderground > 0)) return false;
      if (filters.furnished === "yes" && f.furnished !== true) return false;
      if (filters.furnished === "no" && f.furnished !== false) return false;
      if (filters.pets === "yes" && f.petsAllowed !== true) return false;
      if (filters.pets === "no" && f.petsAllowed !== false) return false;
      if (filters.cargoLift === "1" && !f.cargoLift) return false;
      if (filters.directOwner === "1" && !f.byOwner) return false;
      if (filters.kidsAllowed === "1" && f.adultsOnly === true) return false;

      if (filters.amenities) {
        const want = filters.amenities.split(",").filter(Boolean);
        const have = f.amenities || [];
        if (!want.every((a) => have.includes(a))) return false;
      }
      if (num(filters.builtFrom) != null && (s.builtYear ?? 0) < num(filters.builtFrom)) return false;
      if (num(filters.builtTo) != null && (s.builtYear ?? Infinity) > num(filters.builtTo)) return false;

      if (filters.publication) {
        const age = ageInDays(it.publishedAt);
        if (filters.publication === "3d" && age > 3) return false;
        if (filters.publication === "week" && age > 7) return false;
        if (filters.publication === "month" && age > 30) return false;
        if (filters.publication === "older" && age <= 30) return false;
      }
      return true;
    });

    const goldFirst = (a, b) => (b.gold?.isGold === true) - (a.gold?.isGold === true);
    const byDate = (a, b) => new Date(b.publishedAt) - new Date(a.publishedAt);
    const priceOf = (x) => (x.price?.amount == null ? Infinity : x.price.amount);

    if (sort === "price_asc") list = [...list].sort((a, b) => priceOf(a) - priceOf(b));
    else if (sort === "price_desc") list = [...list].sort((a, b) => priceOf(b) - priceOf(a));
    else if (sort === "new") list = [...list].sort(byDate);
    else if (sort === "size") list = [...list].sort((a, b) => (b.specs?.area ?? 0) - (a.specs?.area ?? 0));
    else if (sort === "availability") list = [...list].sort((a, b) => slotRank(a) - slotRank(b));
    else list = [...list].sort((a, b) => goldFirst(a, b) || byDate(a, b));

    return list;
  }, [data?.items, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const [bounds, setBounds] = useState(null);
  const [hoveredMapIds, setHoveredMapIds] = useState(null);
  const [lockedIds, setLockedIds] = useState(null);
  const [hoveredListingId, setHoveredListingId] = useState(null);
  const [mosaic, setMosaic] = useState(false);
  const [mapFocusNonce, setMapFocusNonce] = useState(0);

  const filterSig = `${JSON.stringify(filters)}|${sort}`;
  useEffect(() => {
    setLockedIds(null);
    setHoveredMapIds(null);
    setBounds(null);
  }, [filterSig]);

  const unmappedCount = useMemo(() => filtered.filter((it) => !isMappable(it)).length, [filtered]);
  const drawerItems = useMemo(
    () => listingsForDrawer({ filtered, bounds, lockedIds, hoveredMapIds }),
    [filtered, bounds, lockedIds, hoveredMapIds]
  );

  useEffect(() => {
    const anchor = window.location.hash;
    if (anchor && !isLoading) {
      const el = document.querySelector(anchor);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isLoading]);

  useEffect(() => {
    if (!mapView || !focusId) return;
    const hit = filtered.find((it) => it.id === focusId);
    setLockedIds(hit && isMappable(hit) ? [focusId] : null);
  }, [mapView, focusId, filtered]);

  useEffect(() => {
    if (!mapView || !focusId) return;
    const el = document.getElementById(`listing-${focusId}`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [mapView, focusId, drawerItems, mapFocusNonce]);

  const cardHover = {
    onHoverStart: (listing) => setHoveredListingId(listing.id),
    onHoverEnd: () => setHoveredListingId(null),
    onViewOnMap: openOnMap,
  };

  const mapFitKey = `${filterSig}|${filtered.length}`;

  return (
    <div className={`${mapView ? "h-screen overflow-hidden" : "min-h-screen"} bg-[#F8FAFC] flex flex-col`}>
      <ResultsTopBar />
      <FilterBar
        filters={filters}
        setFilter={setFilter}
        resetFilters={resetFilters}
        total={filtered.length}
        sort={sort}
        setSort={setSort}
        view={mapView ? "map" : ""}
        setView={setView}
      />

      {!mapView && <AnnouncementBanner />}

      {mapView ? (
        <div className="flex-1 min-h-0">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <Loader2 className="h-8 w-8 animate-spin mb-3" />
              <p className="text-sm">{t("loading")}</p>
            </div>
          ) : (
            <MapResultsLayout
              mosaic={mosaic}
              onMosaicChange={setMosaic}
              list={(
                <div data-testid="map-drawer" className="h-full overflow-y-auto bg-[#F8FAFC]">
                  <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-slate-200 px-4 py-2.5 flex items-center gap-3">
                    <p data-testid="map-area-count" className="text-sm text-slate-600">
                      <span className="font-bold text-slate-900">{drawerItems.length}</span> {t("in_this_area")}
                    </p>
                    {lockedIds?.length ? (
                      <button
                        type="button"
                        data-testid="clear-map-selection"
                        onClick={clearMapSelection}
                        className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-[#7C3AED] hover:text-[#6D28D9]"
                      >
                        <X className="h-3.5 w-3.5" /> {t("clear_map_selection")}
                      </button>
                    ) : null}
                  </div>
                  {unmappedCount > 0 && (
                    <p data-testid="location-unavailable-notice" className="px-4 py-2 text-xs text-slate-500 bg-slate-50 border-b border-slate-100">
                      {unmappedCount} {t("location_unavailable_n")}
                    </p>
                  )}
                  {drawerItems.length === 0 ? (
                    <div className="px-4 py-12 text-center text-sm text-slate-500">
                      {filtered.length === 0 ? t("empty_sub") : t("location_unavailable")}
                    </div>
                  ) : mosaic ? (
                    <div data-testid="results-grid" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
                      {drawerItems.map((it, i) => (
                        <PropertyCard key={it.id} listing={it} index={i} variant="tile" {...cardHover} />
                      ))}
                    </div>
                  ) : (
                    <div data-testid="map-drawer-list" className="flex flex-col gap-3 p-3">
                      {drawerItems.map((it, i) => (
                        <PropertyCard key={it.id} listing={it} index={i} variant="row" {...cardHover} />
                      ))}
                    </div>
                  )}
                </div>
              )}
              map={(
                <ResultsMap
                  listings={filtered}
                  fitKey={mapFitKey}
                  invalidateKey={`${mosaic}`}
                  highlightedListingId={hoveredListingId || focusId || null}
                  focusListingId={focusId || null}
                  focusNonce={mapFocusNonce}
                  onViewport={({ bounds: b }) => setBounds(b)}
                  onHoverIds={(ids) => { if (!lockedIds) setHoveredMapIds(ids); }}
                  onLockIds={(ids) => { setLockedIds(ids); setHoveredMapIds(null); }}
                />
              )}
            />
          )}
        </div>
      ) : (
        <>
          <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-32 text-slate-400">
                <Loader2 className="h-8 w-8 animate-spin mb-3" />
                <p className="text-sm">{t("loading")}</p>
              </div>
            ) : filtered.length === 0 ? (
              <div data-testid="empty-state" className="flex flex-col items-center justify-center py-32 text-center">
                <SearchX className="h-12 w-12 text-slate-300 mb-4" />
                <h2 className="text-xl font-bold text-slate-900">{t("empty_title")}</h2>
                <p className="mt-1 text-slate-500">{t("empty_sub")}</p>
              </div>
            ) : (
              <>
                <div data-testid="results-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {paged.map((it, i) => (
                    <PropertyCard key={it.id} listing={it} index={i} onViewOnMap={openOnMap} />
                  ))}
                </div>
                <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
              </>
            )}
          </main>
          <Footer />
        </>
      )}
    </div>
  );
}
